import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  writeBatch,
  updateDoc,
  limit as qLimit,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

/* ---------- helpers ---------- */

function coalesceTitle(a, qz) {
  // Prefer explicit assignment title if it's good
  if (a?.title && a.title !== "undefined Assignment") return a.title;
  // Prefer any denormalized quizTitle on the assignment
  if (a?.quizTitle && typeof a.quizTitle === "string") return a.quizTitle;
  // Fall back to quiz doc title (if available)
  if (qz?.title) return qz.title;
  return "Untitled Quiz";
}

function normalizeIds(arr) {
  return Array.isArray(arr) ? Array.from(new Set(arr.map(String))).sort() : [];
}

function sameSet(a, b) {
  const A = normalizeIds(a);
  const B = normalizeIds(b);
  if (A.length !== B.length) return false;
  for (let i = 0; i < A.length; i++) if (A[i] !== B[i]) return false;
  return true;
}

function quickOverlapScore(aIds, bIds) {
  const A = new Set(normalizeIds(aIds));
  const B = new Set(normalizeIds(bIds));
  let hit = 0;
  for (const id of A) if (B.has(id)) hit++;
  return A.size ? hit / A.size : 0;
}

/* ---------- API ---------- */

/**
 * Auto migration that:
 * - Fixes title & questionCount if quizId exists,
 * - If quizId is missing, still fixes title from quizTitle,
 * - Attempts to auto-link by matching question IDs / counts,
 * - Returns any remaining "needs manual link" items.
 */
export async function runAssignmentAutoMigration({ signal } = {}) {
  const db = getFirestore();
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  const teacherId = user.uid;

  const asQ = query(
    collection(db, "assignments"),
    where("teacherId", "==", teacherId)
  );
  const snap = await getDocs(asQ);
  const docs = snap.docs;

  let fixed = 0, skipped = 0, errors = 0;
  const missingQuiz = [];

  // Preload my quizzes once for matching when quizId is missing.
  const myQzQ = query(
    collection(db, "quizzes"),
    where("teacherId", "==", teacherId),
    qLimit(300)
  );
  const myQzSnap = await getDocs(myQzQ);
  const myQuizzes = myQzSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  for (const d of docs) {
    if (signal?.aborted) throw new Error("Migration cancelled");
    const a = d.data() || {};

    // Determine if anything is wrong:
    const badTitle = !a.title || a.title === "undefined Assignment" || typeof a.title !== "string";
    const badCount = a.questionCount == null || Number.isNaN(a.questionCount);

    if (!badTitle && !badCount) { skipped++; continue; }

    try {
      // Case A: We HAVE quizId -> canonical repair
      if (a.quizId) {
        const qRef = doc(db, "quizzes", a.quizId);
        const qSnap = await getDoc(qRef);
        const qz = qSnap.exists() ? (qSnap.data() || {}) : null;

        const nextTitle = coalesceTitle(a, qz);
        const nextCount = badCount
          ? (Array.isArray(qz?.questions) ? qz.questions.length : (qz?.questionCount || a.questionCount || 0))
          : a.questionCount;

        if (nextTitle !== a.title || nextCount !== a.questionCount || a.quizTitle !== nextTitle) {
          await updateDoc(d.ref, {
            title: nextTitle,
            quizTitle: nextTitle,
            questionCount: nextCount,
            updatedAt: Date.now()
          });
          fixed++;
        } else {
          skipped++;
        }
        continue;
      }

      // Case B: NO quizId
      // B1) If we have a quizTitle on the assignment, set the title immediately (no linking yet).
      let stagedTitleOnly = false;
      if (badTitle && a.quizTitle && typeof a.quizTitle === "string") {
        await updateDoc(d.ref, {
          title: a.quizTitle,
          updatedAt: Date.now()
        });
        fixed++;
        stagedTitleOnly = true; // title is fixed; we may still try to link below
      }

      // B2) Try to auto-link based on questions
      const aQuestionIds =
        Array.isArray(a.questionIds) ? a.questionIds :
        Array.isArray(a.questions) ? a.questions.map(q => q?.id).filter(Boolean) :
        [];

      const byExactSet = myQuizzes.filter(q =>
        sameSet(
          Array.isArray(q.questionIds) ? q.questionIds :
          (Array.isArray(q.questions) ? q.questions.map(qq => qq?.id).filter(Boolean) : []),
          aQuestionIds
        )
      );

      let chosen = null;

      if (byExactSet.length === 1) {
        chosen = byExactSet[0];
      } else if (!byExactSet.length) {
        // fallback: match by count + overlap >= 0.6 (tunable)
        const candidates = myQuizzes
          .filter(q => {
            const qCount = Array.isArray(q.questions) ? q.questions.length : (q.questionCount || 0);
            return qCount === (a.questionCount || 0);
          })
          .map(q => {
            const qIds = Array.isArray(q.questionIds)
              ? q.questionIds
              : (Array.isArray(q.questions) ? q.questions.map(qq => qq?.id).filter(Boolean) : []);
            return { q, score: quickOverlapScore(aQuestionIds, qIds) };
          })
          .filter(x => x.score >= 0.6)
          .sort((x, y) => y.score - x.score);

        if (candidates.length === 1) chosen = candidates[0].q;
      }

      if (chosen) {
        const nextTitle = stagedTitleOnly
          ? (a.quizTitle || chosen.title || "Untitled Quiz")
          : coalesceTitle(a, chosen);
        const nextCount = Array.isArray(chosen?.questions)
          ? chosen.questions.length
          : (chosen?.questionCount || a.questionCount || 0);

        await updateDoc(d.ref, {
          quizId: chosen.id,
          title: nextTitle,
          quizTitle: nextTitle,
          questionCount: nextCount,
          updatedAt: Date.now()
        });
        fixed++;
      } else {
        // Could not auto-link; record for manual UI
        missingQuiz.push({ id: d.id, ...a });
        // If we already staged a title, we still count that fix and leave linking for manual step
        if (!stagedTitleOnly) skipped++;
      }
    } catch (e) {
      console.error("Migration error", d.id, e);
      errors++;
    }
  }

  return { fixed, skipped, errors, missingQuiz };
}

/**
 * Manual link: when auto-link couldn't decide.
 */
export async function manualLinkAndFixAssignment({ assignmentId, quizId }) {
  const db = getFirestore();
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");

  const aRef = doc(db, "assignments", assignmentId);
  const aSnap = await getDoc(aRef);
  if (!aSnap.exists()) throw new Error("Assignment not found");
  const a = aSnap.data() || {};
  if (a.teacherId !== user.uid) throw new Error("Permission denied");

  const qRef = doc(db, "quizzes", quizId);
  const qSnap = await getDoc(qRef);
  if (!qSnap.exists()) throw new Error("Quiz not found");
  const qz = qSnap.data() || {};

  const nextTitle = coalesceTitle(a, qz);
  const nextCount = Array.isArray(qz.questions) ? qz.questions.length : (qz.questionCount || 0);

  await updateDoc(aRef, {
    quizId,
    title: nextTitle,
    quizTitle: nextTitle,
    questionCount: nextCount,
    updatedAt: Date.now()
  });

  return { fixed: 1, skipped: 0, errors: 0, assignmentId, title: nextTitle, questionCount: nextCount };
}

/**
 * For Dev Panel manual picker
 */
export async function listMyQuizzes({ limit = 50 } = {}) {
  const db = getFirestore();
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  const q = query(collection(db, "quizzes"), where("teacherId", "==", user.uid), qLimit(limit));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export default runAssignmentAutoMigration;
